import { Col, Row, Form } from "react-bootstrap";
import Button from "@mui/material/Button";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import "./Roles.css";
import Accordion from "react-bootstrap/Accordion";
import {
  createRoles,
  geAccountContext,
  getAllRolesDataEdit,
  getPermissionsData,
} from "../../api/rolesApi/rolesApi";
import AddBoxIcon from "@mui/icons-material/AddBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { getAccounts } from "../../api/groupsApi/groupsApi";
import { Checkbox, FormControlLabel, Grid, Tooltip } from "@mui/material";
import { useLoader } from "../../context/LoaderContext";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import LocalStorageUtil from "../../utils/localStorage";
import { appSettings } from "../../config/appSetting";

interface RolesActionsProps {
  businessUnitData: any;
}

const RolesActions: React.FC<RolesActionsProps> = ({ businessUnitData }) => {
  const location = useLocation();
  const { rowData, action } = location.state || {};
  const [permissionsData, setpermissionsData] = useState<any>([]);
  const [accountData, setAccountData] = useState<readonly number[]>([]);
  const [roleDataEdit, setRoleDataEdit] = useState<any>([]);
  const [checkedCategories, setCheckedCategories] = useState<any>([]);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const { setLoading } = useLoader();
  const [isChecked, setIsChecked] = useState<any>([]);
  const navigate = useNavigate();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [isActive, setIsActive] = useState(rowData?.active);
  const [formData, setFormData] = useState({
    accountName: roleDataEdit[0]?.accountName || "",
    roleName: roleDataEdit[0]?.roleName || "",
  });

  const [formErrors, setFormErrors] = useState({
    roleName: "",
  });

  // Helper function to get category permissions
  const getCategoryPermissions = (
    permissionsData: any[],
    categoryId: number
  ): number[] => {
    const category = permissionsData.find(
      (item: any) => item.categoryid === categoryId
    );
    return category?.permissions.map((perm: any) => perm.permissionId) || [];
  };

  // Helper function to update permissions for category
  const updatePermissionsForCategory = (
    prevPermissions: { [key: number]: boolean },
    categoryPermissions: number[],
    isChecked: boolean
  ): { [key: number]: boolean } => {
    const updatedPermissions = { ...prevPermissions };
    categoryPermissions.forEach((permId: number) => {
      updatedPermissions[permId] = isChecked;
    });
    return updatedPermissions;
  };

  // Refactored handleCategoryCheckboxChange function
  const handleCategoryCheckboxChange = (categoryId: any) => {
    setCheckedCategories((prev: { [x: string]: any }) => {
      const isCategoryChecked = !prev[categoryId];
      const categoryPermissions = getCategoryPermissions(
        permissionsData,
        categoryId
      );

      setIsChecked((prevPermissions: { [key: number]: boolean }) => {
        return updatePermissionsForCategory(
          prevPermissions,
          categoryPermissions,
          isCategoryChecked
        );
      });

      return { ...prev, [categoryId]: isCategoryChecked };
    });
  };

  const handleCheckboxChange = (permissionId: number, categoryId: number) => {
    setIsChecked((prev: { [key: number]: boolean }) => {
      const updatedPermissions: { [key: number]: boolean } = { ...prev };

      updatedPermissions[permissionId] = !prev[permissionId];

      const categoryPermissions =
        permissionsData
          .find((item: any) => item.categoryid === categoryId)
          ?.permissions.map((perm: any) => perm.permissionId) || [];

      const allChecked = categoryPermissions.every(
        (permId: number) => updatedPermissions[permId]
      );

      setCheckedCategories((prevCategories: { [key: number]: boolean }) => ({
        ...prevCategories,
        [categoryId]: allChecked,
      }));

      return updatedPermissions;
    });
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (name === "roleName") {
      validateText(value, name);
    }
    if (name === "accountName") {
      getAllAccountsName();
      getAccountContextData();
      getAllPermissionsData();
    }
  };

  const handleToggle = (key: string, event: React.MouseEvent) => {
    if ((event.target as HTMLElement).tagName === "INPUT") {
      return;
    }
    setActiveKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleExpandAll = () => {
    const allKeys = permissionsData.map((item: any) =>
      item.categoryid.toString()
    );
    setActiveKeys(allKeys);
  };

  const handleCollapseAll = () => {
    setActiveKeys([]);
  };

  const roleId = rowData ? rowData.id : 0;

  const getAccountContextData = async () => {
    setLoading(true);
    const payload = {
      businessUnitId:
        businessUnitData?.businessUnitId ||
        LocalStorageUtil.getItem("businessUnitId"),
      contextName:
        businessUnitData?.contextName ||
        LocalStorageUtil.getItem("contextName"),
    };
    try {
      await geAccountContext(payload);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  const getRolesDataEdit = async () => {
    setLoading(true);
    const payload = {
      RoleDataModelObj: { RoleId: roleId },
      contextName:
        businessUnitData?.contextName ||
        LocalStorageUtil.getItem("contextName"),
    };
    try {
      const data = await getAllRolesDataEdit(payload);
      setRoleDataEdit(data);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (roleDataEdit && roleDataEdit.length > 0) {
      setFormData({
        accountName: roleDataEdit[0]?.accountName || "",
        roleName: roleDataEdit[0]?.roleName || "",
      });
      if (action === "Duplicate") {
        setFormData({
          accountName: roleDataEdit[0]?.accountName || "",
          roleName: "",
        });
      }
    }
  }, [roleDataEdit]);

  const getAllPermissionsData = async () => {
    setLoading(true);
    const payload = {
      contextName:
        businessUnitData?.contextName ||
        LocalStorageUtil.getItem("contextName"),
      objPermission: {},
    };
    try {
      const data = await getPermissionsData(payload);
      const filteredData = data.filter(
        (item: any) => !appSettings.excludedCategories.includes(item.categoryid)
      );
      setpermissionsData(filteredData);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  const getAllAccountsName = async () => {
    setLoading(true);
    const payload = {
      contextName:
        businessUnitData?.contextName ||
        LocalStorageUtil.getItem("contextName"),
    };
    try {
      const data = await getAccounts(payload);
      setAccountData(data);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (action === "Edit" || action === "Duplicate") {
      getAllPermissionsData();
      getAccountContextData();
    }
    getRolesDataEdit();
    getAllAccountsName();
  }, []);

  useEffect(() => {
    if (roleDataEdit?.length > 0) {
      const rolePermissions = new Set(
        roleDataEdit[0].permission.map((p: any) => p.permissionId)
      );
      const initialCheckedState = permissionsData.reduce(
        (acc: any, category: any) => {
          category.permissions.forEach((permission: any) => {
            acc[permission.permissionId] = rolePermissions.has(
              permission.permissionId
            );
          });
          return acc;
        },
        {}
      );

      setIsChecked(initialCheckedState);
    }
  }, [roleDataEdit, permissionsData]);

  useEffect(() => {
    const updatedCheckedCategories = permissionsData.reduce(
      (acc: any, category: any) => {
        const categoryPermissions = category.permissions.map(
          (p: any) => p.permissionId
        );

        const areAllPermissionsChecked = categoryPermissions.every(
          (id: any) => isChecked[id]
        );

        acc[category.categoryid] = areAllPermissionsChecked;
        return acc;
      },
      {}
    );

    setCheckedCategories(updatedCheckedCategories);
  }, [isChecked, permissionsData]);

  const validateText = (text: string, fieldName: keyof typeof formErrors) => {
    const textPattern = /^[a-zA-Z\s]+$/;
    if (!textPattern.exec(text)) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "Please enter alphabets only",
      }));
    } else {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "",
      }));
    }
  };

  const handleConfirmDialogClose = (confirm: boolean) => {
    try {
      setConfirmDialogOpen(false);
      if (confirm) {
        navigate("/roleview");
      } else {
        setConfirmDialogOpen(false);
      }
    } catch (error) {
      console.error("Error in handleConfirmDialogClose", error);
    }
  };

  const handelCancel = () => {
    setConfirmDialogOpen(true);
  };

  const handleCreate = async () => {
    const RoleId = (() => {
      switch (action) {
        case "Edit":
          return roleId;
        case "Duplicate":
          return 0;
        default:
          return null;
      }
    })();
    const payload = {
      contextName: LocalStorageUtil.getItem("contextName"),
      RoleModelObj: {
        RoleId: RoleId,
        RoleName: formData.roleName,
        PermissionId: JSON.stringify(
          Object.entries(isChecked)
            .filter(([key, value]) => value === true)
            .map(([key]) => Number(key))
        ),
        AccountId: LocalStorageUtil.getItem("accountId"),
        Active: action === "Edit" || action === "Duplicate" ? isActive : true,
        CreatedBy: LocalStorageUtil.getItem("userId"),
        CreatedDate: action === "Edit" ? rowData?.createdDate : null,
      },
    };
    try {
      await createRoles(payload);
      navigate("/roleview");
    } catch (error) {
      console.log("Error fetching data", error);
    }
  };

  const handleStatusChange = () => {
    setIsActive((prevIsActive: any) => {
      const updatedStatus = !prevIsActive;
      return updatedStatus;
    });
  };

  return (
    <div style={{ overflow: "hidden" }}>
      {action === "Edit" ? (
        <NavigationBar
          menuTitle="Configuration"
          title="User Management"
          screenTitle="Roles"
          text="Roles - Update Role"
        />
      ) : (
        <NavigationBar
          menuTitle="Configuration"
          title="User Management"
          screenTitle="Roles"
          text="Roles - Create New Role"
        />
      )}
      <div className="ag-theme-quartz-dark" style={{ marginLeft: "4vh" }}>
        <div
          className="col-sm-5"
          style={{
            width: "97%",
            textAlign: "right",
            margin: "10px",
          }}
        >
          {action === "Edit" && (
            <Tooltip
              title={
                rowData.active
                  ? "Click to make the group inactive"
                  : "Click to make the group active"
              }
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isActive}
                    onChange={handleStatusChange}
                    color="primary"
                    size="small"
                  />
                }
                label={isActive ? "Active" : "In-Active"}
                style={{
                  margin: "1px 3px",
                  textTransform: "none",
                  fontSize: "8px",
                  color: isActive ? "#154360" : "#7B241C",
                  backgroundColor: isActive ? "#D6EAF8" : "#FADBD8",
                  padding: "3px 6px",
                  borderRadius: "3px",
                  alignItems: "center",
                  height: "2.5em",
                }}
              />
            </Tooltip>
          )}
        </div>
        <h4
          className="form-heading"
          style={{ color: "var(--aggrid-text-color)", fontSize: "12px" }}
        >
          BASIC INFORMATION
        </h4>
        <Form>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group controlId="formCustomer">
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  Customer<span className="required">*</span>
                </Form.Label>
                <Form.Select
                  name="accountName"
                  disabled={action === "Edit" || action === "Duplicate"}
                  value={formData.accountName}
                  onChange={handleChange}
                  className="select-dropdown"
                  style={{ fontSize: "11px" }}
                >
                  <option value="">Select customer</option>
                  {accountData.map((account: any) => (
                    <option key={account.id} value={account.accountName}>
                      {account.accountName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="formGroupName">
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  Role Name<span className="required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter group name"
                  name="roleName"
                  value={formData.roleName}
                  onChange={handleChange}
                  required
                  isInvalid={!!formErrors.roleName}
                  className="input-box"
                  style={{ fontSize: "11px" }}
                />
                {formErrors.roleName && (
                  <Form.Control.Feedback type="invalid">
                    {formErrors.roleName}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Label className="input-labels">
                <span
                  className="upperCase"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  Permissions
                </span>
                <span className="required">*</span>
              </Form.Label>
            </Col>
            <Col md={9} className="d-flex justify-content-end">
              {" "}
              <Button
                variant="contained"
                className="expand-button me-4"
                onClick={handleExpandAll}
                style={{
                  fontSize: "11px",
                  borderRadius: "4px",
                  border: "1px solid #858c94",
                  height: "25px",
                }}
              >
                Expand All
              </Button>
              <Button
                variant="contained"
                className="expand-button"
                onClick={handleCollapseAll}
                style={{
                  fontSize: "11px",
                  borderRadius: "4px",
                  border: "1px solid #858c94",
                  height: "25px",
                  marginRight: "1.5em",
                }}
              >
                Collapse All
              </Button>
            </Col>
          </Row>
        </Form>
        <div
          className="accordian-div"
          style={{ fontSize: "11px", marginRight: "1.5em" }}
        >
          <Accordion activeKey={activeKeys} className="custom-accordion">
            {permissionsData?.map((item: any) => (
              <Accordion.Item
                eventKey={item?.categoryid.toString()}
                key={item?.categoryid}
                style={{ marginTop: "5px" }}
              >
                <Accordion.Header
                  className="accordian-head-bg"
                  style={{ fontSize: "11px" }}
                  onClick={(e) => handleToggle(item?.categoryid.toString(), e)}
                >
                  <Form.Check
                    type="checkbox"
                    checked={checkedCategories[item?.categoryid] || false}
                    onChange={() =>
                      handleCategoryCheckboxChange(item?.categoryid)
                    }
                    className="accordinag-heding"
                    label={item.category}
                  />
                  {activeKeys?.includes(item?.categoryid.toString()) ? (
                    <IndeterminateCheckBoxIcon className="accordian-icons" />
                  ) : (
                    <AddBoxIcon className="accordian-icons" />
                  )}
                </Accordion.Header>
                <Accordion.Body>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "space-between",
                    }}
                  >
                    {item.permissions.map((permission: any) => (
                      <div
                        key={permission.permissionId}
                        style={{
                          flex: "0 0 calc(25% - 20px)",
                          marginBottom: "20px",
                        }}
                      >
                        <Row>
                          <Col md={12}>
                            <Form.Check
                              type="checkbox"
                              label={permission.permissionName}
                              checked={
                                isChecked[permission.permissionId] || false
                              }
                              onChange={() =>
                                handleCheckboxChange(
                                  permission.permissionId,
                                  item?.categoryid
                                )
                              }
                            />
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>
        <Grid
          item
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
          }}
        >
          {action === "Edit" ? (
            <Button
              variant="contained"
              className="update-button"
              onClick={handleCreate}
              style={{
                fontSize: "11px",
                borderRadius: "4px",
                border: "1px solid #858c94",
                height: "25px",
              }}
            >
              Update
            </Button>
          ) : (
            <Button
              variant="contained"
              className="update-button"
              onClick={handleCreate}
            >
              Create
            </Button>
          )}
          <Button
            variant="contained"
            className="cancel-button"
            onClick={handelCancel}
            style={{ marginRight: "1.5em" }}
          >
            Cancel
          </Button>
        </Grid>
      </div>
      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        title={`Roles -  ${action === "Edit" ? "Update Role" : "Create Role"}`}
        content="Are you sure you want to Cancel?"
      />
    </div>
  );
};

export default RolesActions;
